import axios from 'axios';

type AuthSessionPayload = {
  accessToken?: string | null;
  deviceBindingToken?: string | null;
  deviceId?: string | null;
};

type AuthFailureReason =
  | 'refresh_failed'
  | 'refresh_replay'
  | 'session_compromised'
  | 'device_mismatch'
  | 'device_binding_failed';

type ApiAuthHandler = {
  onAuthFailure?: (reason: AuthFailureReason, detail?: string) => void;
};

let authSession: Required<AuthSessionPayload> = {
  accessToken: null,
  deviceBindingToken: null,
  deviceId: null
};

let authHandler: ApiAuthHandler = {};
let refreshPromise: Promise<string | null> | null = null;

const DEVICE_KEY = 'auth_device_id_v1';
const BINDING_KEY = 'auth_device_binding_v1';
const LAST_CLOSE_KEY = 'auth_last_close_at_v1';
const MANUAL_LOGOUT_KEY = 'auth_manual_logout_v1';
const SILENT_RESUME_WINDOW_MS = 5 * 60 * 60 * 1000;

export const getApiBaseUrl = (): string => process.env.REACT_APP_API_BASE_URL || '';

export const getFullApiUrl = (endpoint: string): string => `${getApiBaseUrl()}${endpoint}`;

export const getStoredDeviceId = (): string => {
  const existing = localStorage.getItem(DEVICE_KEY);
  if (existing) return existing;
  const randomId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}_${Math.random().toString(16).slice(2)}`;
  const created = `dev_${randomId}`;
  localStorage.setItem(DEVICE_KEY, created);
  return created;
};

export const getStoredDeviceBindingToken = (): string | null => localStorage.getItem(BINDING_KEY);

export const setStoredDeviceBindingToken = (token: string | null) => {
  if (!token) {
    localStorage.removeItem(BINDING_KEY);
    return;
  }
  localStorage.setItem(BINDING_KEY, token);
};

export const clearDeviceBinding = () => {
  localStorage.removeItem(BINDING_KEY);
};

export const markAppClosedAt = (value: number = Date.now()) => {
  localStorage.setItem(LAST_CLOSE_KEY, String(value));
};

export const clearAppClosedAt = () => {
  localStorage.removeItem(LAST_CLOSE_KEY);
};

export const markManualLogout = () => {
  localStorage.setItem(MANUAL_LOGOUT_KEY, '1');
  markAppClosedAt();
};

export const clearManualLogout = () => {
  localStorage.removeItem(MANUAL_LOGOUT_KEY);
};

export const canAttemptSilentResume = () => {
  if (localStorage.getItem(MANUAL_LOGOUT_KEY) === '1') {
    return false;
  }
  const raw = localStorage.getItem(LAST_CLOSE_KEY);
  if (!raw) return true;
  const lastCloseAt = Number(raw);
  if (!Number.isFinite(lastCloseAt) || lastCloseAt <= 0) return true;
  return Date.now() - lastCloseAt <= SILENT_RESUME_WINDOW_MS;
};

export const setApiAuthHandler = (handler: ApiAuthHandler) => {
  authHandler = handler;
};

export const setAuthSession = (payload: AuthSessionPayload) => {
  authSession = {
    accessToken: payload.accessToken ?? authSession.accessToken,
    deviceBindingToken: payload.deviceBindingToken ?? authSession.deviceBindingToken,
    deviceId: payload.deviceId ?? authSession.deviceId
  };
  if (payload.deviceBindingToken !== undefined) {
    setStoredDeviceBindingToken(payload.deviceBindingToken || null);
  }
};

export const clearAuthSession = () => {
  authSession = {
    accessToken: null,
    deviceBindingToken: null,
    deviceId: getStoredDeviceId()
  };
};

export const getAccessToken = () => authSession.accessToken;
export const getDeviceId = () => authSession.deviceId || getStoredDeviceId();
export const getDeviceBindingToken = () =>
  authSession.deviceBindingToken || getStoredDeviceBindingToken();

export const apiClient = axios.create({
  baseURL: getApiBaseUrl(),
  withCredentials: true
});

type RetryableRequestConfig = { _retry?: boolean; url?: string; headers?: any } & any;

const isAuthRoute = (url: string = '') => url.includes('/api/auth/token/refresh');

const isRefreshCompromisedError = (message: string) =>
  /replay detected|session revoked/i.test(message || '');

const isDeviceMismatchError = (message: string) =>
  /device mismatch|binding mismatch|device binding verification failed/i.test(message || '');

const isStepUpRequiredError = (message: string) =>
  /step-up token required|step-up token is not valid|step-up authentication required/i.test(message || '');

async function performRefresh(): Promise<string | null> {
  if (!canAttemptSilentResume()) {
    authHandler.onAuthFailure?.('refresh_failed', 'Session expired. Please login again.');
    return null;
  }
  const deviceId = getDeviceId();
  const deviceBindingToken = getDeviceBindingToken();
  try {
    const response = await axios.post(
      getFullApiUrl('/api/auth/token/refresh'),
      { deviceId, deviceBindingToken },
      {
        withCredentials: true,
        headers: {
          'x-device-id': deviceId,
          ...(deviceBindingToken ? { 'x-device-binding': deviceBindingToken } : {})
        }
      }
    );

    const data = response.data as {
      accessToken: string;
      deviceBindingToken?: string;
    };

    setAuthSession({
      accessToken: data.accessToken,
      deviceBindingToken: data.deviceBindingToken || deviceBindingToken,
      deviceId
    });

    return data.accessToken;
  } catch (error: any) {
    const message = error?.response?.data?.message || '';
    if (isRefreshCompromisedError(message)) {
      authHandler.onAuthFailure?.('refresh_replay', message);
    } else if (isDeviceMismatchError(message)) {
      authHandler.onAuthFailure?.('device_mismatch', message);
      clearDeviceBinding();
    } else if (/invalid refresh token/i.test(message)) {
      authHandler.onAuthFailure?.('session_compromised', message);
    } else {
      authHandler.onAuthFailure?.('refresh_failed', message);
    }
    return null;
  }
}

const attachInterceptors = (instance: any) => {
  instance.interceptors.request.use((config: any) => {
    const deviceId = getDeviceId();
    const deviceBindingToken = getDeviceBindingToken();
    const token = getAccessToken();

    config.headers = config.headers || {};
    config.headers['x-device-id'] = deviceId;
    if (deviceBindingToken) {
      config.headers['x-device-binding'] = deviceBindingToken;
    }
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  });

  instance.interceptors.response.use(
    (response: any) => response,
    async (error: any) => {
    const originalRequest = (error.config || {}) as RetryableRequestConfig;
    const status = error.response?.status;
    const message = ((error.response?.data as any)?.message || '').toString();

    if (status === 403 && isStepUpRequiredError(message)) {
      const enhanced = error as any;
      enhanced.requiresStepUp = true;
      enhanced.stepUpOperation = ((originalRequest.headers || {})['x-step-up-operation'] as string) || null;
      return Promise.reject(enhanced);
    }

    if (status === 403 && isDeviceMismatchError(message)) {
      authHandler.onAuthFailure?.('device_binding_failed', message);
      clearDeviceBinding();
      return Promise.reject(error);
    }

    if (status !== 401 || originalRequest._retry || isAuthRoute(originalRequest.url || '')) {
      return Promise.reject(error);
    }

    if (!refreshPromise) {
      refreshPromise = performRefresh().finally(() => {
        refreshPromise = null;
      });
    }

    const newToken = await refreshPromise;
    if (!newToken) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;
    originalRequest.headers = originalRequest.headers || {};
    originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return instance(originalRequest);
    }
  );
};

axios.defaults.baseURL = getApiBaseUrl();
axios.defaults.withCredentials = true;
attachInterceptors(axios);
attachInterceptors(apiClient);
